import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { getOrganizationSetup, isSetupUnavailable, resolveOrganizationSetupTask } from '@/data/organizationSetup';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { canManageOrganizationSetup } from '@/config/organizationSetup';

export default function useOrganizationSetup(user, { enabled = true, poll = false } = {}) {
    const queryClient = useQueryClient();
    const organizationID = user?.organizationID;
    const queryKey = QueryKeys.organizationSetup.state(organizationID, user?.accountID);
    const query = useQuery({
        queryKey,
        queryFn: () => getOrganizationSetup(organizationID),
        enabled: enabled && !!organizationID && canManageOrganizationSetup(user),
        staleTime: 10000,
        refetchInterval: query => poll && !isSetupUnavailable(query.state.error) && query.state.error?.code !== '42501' ? 15000 : false,
        refetchIntervalInBackground: false,
        retry: (count, error) => !isSetupUnavailable(error) && error?.code !== '42501' && count < 2,
    });
    const resolve = useMutation({
        mutationKey: ['organizationSetupResolution', organizationID],
        mutationFn: ({ taskID, status }) => resolveOrganizationSetupTask({ organizationID, taskID, status }),
        onSuccess: state => {
            queryClient.setQueryData(queryKey, state);
            queryClient.invalidateQueries({ queryKey: ['organizationSetup', organizationID] });
        },
        onError: error => Toast.show({ type: 'error', text2: error?.message || 'Setup progress could not be saved. Try again.' }),
    });
    // Spreading the tracked result subscribes every caller to poll metadata.
    // Forward UI fields lazily so data-only consumers ignore unchanged polls.
    return {
        get data() { return query.data; },
        get isPending() { return query.isPending; },
        get isError() { return query.isError; },
        get isFetchedAfterMount() { return query.isFetchedAfterMount; },
        get refetch() { return query.refetch; },
        get unavailable() { return isSetupUnavailable(query.error); },
        resolve,
    };
}
